import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { XIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { addNotifyEmail, removeNotifyEmail } from './pricelistCaseNotifyEmails';

export function PricelistCaseNotifyEmailsField({
  emails,
  disabled,
  onChange,
}: {
  emails: readonly string[];
  disabled?: boolean;
  onChange: (emails: string[]) => void;
}) {
  const gt = useGT();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  function commitDraft() {
    const result = addNotifyEmail(emails, draft);
    if ('error' in result) {
      setError(
        result.error === 'duplicate'
          ? gt('That email is already on the list', {
              context:
                'validation when adding a follow-up email already on a price change',
            })
          : result.error === 'limit'
            ? gt('Too many follow-up emails', {
                context:
                  'validation when a price change already has the maximum extra emails',
              })
            : gt('Enter a valid email address', {
                context:
                  'validation when adding an invalid follow-up email on a price change',
              }),
      );
      return;
    }
    setDraft('');
    setError(null);
    onChange(result.emails);
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor="pricelist-case-notify-emails" className="text-xs">
        <T context="field label for extra emails that get follow-up reminders on a price change">
          Also email
        </T>
      </Label>
      <div className="flex flex-wrap gap-1.5">
        {emails.map((email) => (
          <Badge
            key={email}
            variant="secondary"
            className="gap-1 pr-1 font-normal"
          >
            <span className="max-w-[9rem] truncate">{email}</span>
            <button
              type="button"
              className="rounded-sm p-0.5 hover:bg-accent"
              disabled={disabled}
              onClick={() => onChange(removeNotifyEmail(emails, email))}
              aria-label={gt('Remove {email}', {
                context:
                  'accessible label for removing an extra follow-up email from a price change',
                email,
              })}
            >
              <XIcon className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-1.5">
        <Input
          id="pricelist-case-notify-emails"
          type="email"
          value={draft}
          disabled={disabled}
          className="h-8"
          placeholder={gt('name@example.com', {
            context:
              'placeholder for adding an extra follow-up email on a price change',
          })}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            commitDraft();
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="h-8"
          disabled={disabled || draft.trim().length === 0}
          onClick={commitDraft}
        >
          <T context="button that adds an extra follow-up email on a price change">
            Add
          </T>
        </Button>
      </div>
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          <T context="hint under extra follow-up emails on a price change">
            People without a Gently login still get the due-date email.
          </T>
        </p>
      )}
    </div>
  );
}
